import { apiService } from '../api';

// ─────────────────────────────────────────────────────────────────────────────
// Shapes mirror the backend service's return types verbatim — keep in sync
// with `backend/src/services/teachingLoadService.ts`.
// ─────────────────────────────────────────────────────────────────────────────

export interface ClassTeacherRow {
  // null = virtual row for a class without sections — picker is disabled.
  sectionInstanceId: string | null;
  sessionId: string;
  classId: string;
  className: string;
  sectionName: string | null;
  capacity: number | null;
  room: string | null;
  classTeacher: { _id: string; name: string } | null;
  isClassWithoutSections?: boolean;
}

export interface SubjectAssignmentRow {
  _id: string;
  sessionId: string;
  classId: string;
  className: string;
  sectionInstanceId: string | null;
  sectionName: string | null;
  subjectId: string;
  subjectName: string;
  teacher: { _id: string; name: string };
}

export interface SubjectAssignmentGridCell {
  // null = class-wide cell (the class has no sections in this session).
  // The TSA write uses sectionInstanceId = null for these.
  sectionInstanceId: string | null;
  sectionName: string | null;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  /** One teacher, or several who split the subject's periods. Empty when unassigned. */
  teachers: CellTeacher[];
  classOrder: number;
  /** The curriculum entry behind the cell: the weekly periods and doubles a split must add up to. */
  curriculum: {
    _id: string;
    periodsPerWeek: number;
    doublePeriods: number;
    maxPerDay: number | null;
    roomType: string | null;
    preferredTime: 'any' | 'morning' | 'afternoon';
  };
}

/** A teacher on a cell. `periods`/`doublePeriods` are set only when the subject is split. */
export interface CellTeacher {
  _id: string;
  name: string;
  periods: number | null;
  doublePeriods: number | null;
}

/** What a cell is saved as: each teacher and, for a split, their share. */
export interface SubjectTeacherShare {
  teacherId: string;
  periods?: number | null;
  doublePeriods?: number | null;
}

export interface CopyResult {
  classTeachersCopied: number;
  classTeachersSkipped: number;
  subjectAssignmentsCopied: number;
  subjectAssignmentsSkipped: number;
}

export interface TeacherOption {
  _id: string;
  name: string;
  roleName: string;
}

export interface MyTeachingLoads {
  classTeacherOf: Array<{
    sessionId: string; sessionName: string;
    classId: string;   className: string;
    // null = sectionless class — the teacher is homeroom for the whole class.
    sectionInstanceId: string | null; sectionName: string | null;
    room: string | null;
  }>;
  subjectsTaught: Array<{
    assignmentId: string;
    sessionId: string;          sessionName: string;
    classId: string;            className: string;
    sectionInstanceId: string | null;  sectionName: string | null;
    subjectId: string;          subjectName: string;
  }>;
}

export const teachingLoadsApi = {
  // Teachers eligible for the class/subject teacher pickers. Gated by
  // teaching_loads.read on the server, so this replaces the old
  // /admin/users call which required users.read.
  listTeachers: async (): Promise<TeacherOption[]> => {
    const res = await apiService.get<TeacherOption[]>('/teaching-loads/teachers');
    return res.data ?? [];
  },

  // Class teachers (one per SectionInstance)
  listClassTeachers: async (sessionId: string): Promise<ClassTeacherRow[]> => {
    const res = await apiService.get<ClassTeacherRow[]>('/teaching-loads/class-teachers', { sessionId });
    return res.data ?? [];
  },

  // Two slot shapes, one method:
  //  - Sectioned class → pass { sectionInstanceId }
  //  - Sectionless class → pass { classId, sessionId } (homeroom is stored
  //    on ClassHomeroom instead of a per-section row).
  setClassTeacher: async (
    slot: { sectionInstanceId: string }
        | { sectionInstanceId: null; classId: string; sessionId: string },
    teacherId: string | null
  ): Promise<ClassTeacherRow> => {
    if (slot.sectionInstanceId !== null) {
      const res = await apiService.put<ClassTeacherRow>(
        `/teaching-loads/class-teachers/${slot.sectionInstanceId}`,
        { teacherId }
      );
      return res.data!;
    }
    const res = await apiService.put<ClassTeacherRow>(
      '/teaching-loads/class-teachers',
      { classId: slot.classId, sessionId: slot.sessionId, teacherId }
    );
    return res.data!;
  },

  // Subject teachers
  listSubjectAssignments: async (params: {
    sessionId: string;
    classId?: string;
    sectionInstanceId?: string | null;
    subjectId?: string;
  }): Promise<SubjectAssignmentRow[]> => {
    const query: Record<string, unknown> = { sessionId: params.sessionId };
    if (params.classId) query.classId = params.classId;
    if (params.sectionInstanceId === null) query.sectionInstanceId = 'null';
    else if (params.sectionInstanceId) query.sectionInstanceId = params.sectionInstanceId;
    if (params.subjectId) query.subjectId = params.subjectId;
    const res = await apiService.get<SubjectAssignmentRow[]>('/teaching-loads/subject-teachers', query);
    return res.data ?? [];
  },

  subjectAssignmentGrid: async (sessionId: string, classId?: string): Promise<SubjectAssignmentGridCell[]> => {
    const query: Record<string, unknown> = { sessionId };
    if (classId) query.classId = classId;
    const res = await apiService.get<SubjectAssignmentGridCell[]>('/teaching-loads/subject-teachers/grid', query);
    return res.data ?? [];
  },

  setSubjectTeachers: async (input: {
    sessionId: string;
    classId: string;
    sectionInstanceId?: string | null;
    subjectId: string;
    teachers: SubjectTeacherShare[];
  }): Promise<CellTeacher[]> => {
    const res = await apiService.put<CellTeacher[]>('/teaching-loads/subject-teachers', input);
    return res.data ?? [];
  },

  copyFromPreviousSession: async (input: {
    sourceSessionId: string;
    targetSessionId: string;
  }): Promise<CopyResult> => {
    const res = await apiService.post<CopyResult>('/teaching-loads/copy-from-previous-session', input);
    return res.data!;
  },

  // "My loads" — for the logged-in teacher
  getMine: async (sessionId?: string): Promise<MyTeachingLoads> => {
    const res = await apiService.get<MyTeachingLoads>('/teaching-loads/mine', sessionId ? { sessionId } : undefined);
    return res.data ?? { classTeacherOf: [], subjectsTaught: [] };
  }
};
